import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import ModuleHeader from "../components/ModuleHeader";
import { Table } from "antd";
import ReactQRCode from "react-qr-code";
import { rem2px, REM_CONFIG } from "@/utils/fontSize";
import { mmsApi } from "@/api/mmsApi";
// 弹窗固定宽高（二维码180+内边距）
const POPUP_WIDTH = 212;
const POPUP_HEIGHT = 244;
const OFFSET = 15;
const StoragePeriodStatistics = ({ className = "", }) => {
    const ROW_HEIGHT = rem2px(REM_CONFIG.rowHeight);
    const [tableHeight, setTableHeight] = useState("auto");
    const [isScrolling, setIsScrolling] = useState(true);
    const tableContainerRef = useRef(null);
    const scrollTimer = useRef(null);
    // 缓存滚动容器，优化性能
    const scrollContainerRef = useRef(null);
    // 二维码弹窗状态
    const [showQrPopup, setShowQrPopup] = useState(false);
    const [currentAssetCode, setCurrentAssetCode] = useState("");
    const [popupPos, setPopupPos] = useState({ x: 0, y: 0 });
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    // 二维码鼠标移入：默认左侧弹出，空间不足自动切换右侧防遮挡
    const handleQrMouseEnter = (e, code) => {
        const mouseX = e.clientX;
        const mouseY = e.clientY;
        setCurrentAssetCode(code);
        let left = mouseX - POPUP_WIDTH - OFFSET;
        // 左侧空间不足，改为右侧展示
        if (left < 10) {
            left = mouseX + OFFSET;
        }
        // 左右边界兜底
        left = Math.max(10, Math.min(left, window.innerWidth - POPUP_WIDTH - 10));
        let top = mouseY + OFFSET;
        // 下方空间不足，向上偏移
        if (top + POPUP_HEIGHT > window.innerHeight - 10) {
            top = mouseY - POPUP_HEIGHT - OFFSET;
        }
        top = Math.max(10, top);
        setPopupPos({ x: left, y: top });
        setShowQrPopup(true);
        setIsScrolling(false);
    };
    // 二维码鼠标移出
    const handleQrMouseLeave = () => {
        setShowQrPopup(false);
        setIsScrolling(true);
    };
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await mmsApi.getRealTimeInventory();
                setStats(res.data || []);
            }
            catch (err) {
                console.error("获取实时库存失败：", err);
                setStats([]);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);
    const columns = [
        {
            title: "物料编号",
            dataIndex: "mcode",
            key: "mcode",
            width: "30%",
            align: "center",
            ellipsis: true,
        },
        {
            title: "物资名称",
            dataIndex: "assetName",
            key: "assetName",
            width: "30%",
            align: "center",
            ellipsis: true,
        },
        {
            title: "数量",
            dataIndex: "qty",
            key: "qty",
            width: "20%",
            align: "end",
            ellipsis: true,
        },
        {
            title: "二维码",
            dataIndex: "assetCode",
            key: "assetCode",
            width: "20%",
            align: "center",
            render: (code) => (_jsx("div", { style: {
                    width: "100%",
                    height: "0.34rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                }, onMouseEnter: (e) => handleQrMouseEnter(e, code), onMouseLeave: handleQrMouseLeave, children: _jsx(ReactQRCode, { value: code, size: 20, fgColor: "#00ffff", bgColor: "transparent" }) })),
        },
    ];
    useEffect(() => {
        const calculateTableHeight = () => {
            if (!tableContainerRef.current)
                return;
            const container = tableContainerRef.current;
            const parent = container.parentElement;
            if (!parent)
                return;
            // 缓存滚动容器
            scrollContainerRef.current =
                container.querySelector(".ant-table-body");
            const header = container.previousElementSibling;
            const headerHeight = header?.offsetHeight || 0;
            const unitText = container.querySelector("p");
            const unitTextHeight = unitText ? unitText.offsetHeight : 0;
            const topSpacing = rem2px(REM_CONFIG.spacing.top);
            const totalTopHeight = headerHeight + unitTextHeight + topSpacing;
            const parentHeight = parent.offsetHeight;
            const availableHeight = parentHeight - totalTopHeight;
            const contentHeight = stats.length * ROW_HEIGHT;
            if (contentHeight <= availableHeight || availableHeight < rem2px(0.1)) {
                setTableHeight("auto");
            }
            else {
                setTableHeight(Math.floor(availableHeight));
            }
        };
        calculateTableHeight();
        const resizeObserver = new ResizeObserver(calculateTableHeight);
        requestAnimationFrame(() => {
            if (tableContainerRef.current) {
                resizeObserver.observe(tableContainerRef.current);
            }
        });
        return () => resizeObserver.disconnect();
    }, [stats, ROW_HEIGHT]);
    // ✅ 优化：统一自动滚动逻辑（和其他组件完全一致）
    useEffect(() => {
        if (scrollTimer.current) {
            clearInterval(scrollTimer.current);
            scrollTimer.current = null;
        }
        // 滚动条件判断
        if (tableHeight === "auto" || !isScrolling || stats.length <= 1) {
            return;
        }
        // 使用缓存的滚动容器
        if (!scrollContainerRef.current)
            return;
        const scrollContainer = scrollContainerRef.current;
        // 初始滚动到底部
        scrollContainer.scrollTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight;
        scrollTimer.current = setInterval(() => {
            const { scrollTop, clientHeight, scrollHeight } = scrollContainer;
            const scrollStep = REM_CONFIG.spacing.scrollStep;
            let newScrollTop = scrollTop + scrollStep;
            if (newScrollTop + clientHeight >= scrollHeight - 1) {
                newScrollTop = 0;
            }
            scrollContainer.scrollTop = newScrollTop;
        }, 50);
        return () => {
            if (scrollTimer.current) {
                clearInterval(scrollTimer.current);
            }
        };
    }, [tableHeight, isScrolling, stats.length]);
    const handleMouseEnter = () => {
        setIsScrolling(false);
    };
    const handleMouseLeave = () => {
        setIsScrolling(true);
    };
    return (_jsxs("div", { className: `relative bg-gradient-to-br from-blue-900/10 to-slate-900/20 flex flex-col ${className}`, style: { width: "100%", boxSizing: "border-box" }, children: [_jsx(ModuleHeader, { title: "\u5B9E\u65F6\u5E93\u5B58" }), _jsxs("div", { ref: tableContainerRef, className: "custom-table-container flex-1 w-full h-full min-h-0", style: {
                    minHeight: 0,
                    width: "100%",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    paddingLeft: `${REM_CONFIG.spacing.paddingX}rem`,
                    paddingRight: `${REM_CONFIG.spacing.paddingX}rem`,
                }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsx(Table, { columns: columns, dataSource: stats, rowKey: "stockId", pagination: false, loading: loading, scroll: {
                            y: tableHeight,
                            x: "100%",
                        }, style: {
                            width: "100%",
                            tableLayout: "fixed",
                            borderCollapse: "collapse",
                        }, onRow: (record, index) => ({
                            style: {
                                backgroundColor: index % 2 === 0 ? "transparent" : "#062654",
                                height: `${ROW_HEIGHT}px`,
                            },
                        }), locale: { emptyText: "" } }), showQrPopup && (_jsxs("div", { style: {
                            position: "fixed",
                            left: popupPos.x,
                            top: popupPos.y,
                            width: POPUP_WIDTH,
                            background: "rgba(6, 42, 88, 0.95)",
                            padding: "16px",
                            borderRadius: "6px",
                            boxShadow: "0 0 12px rgba(0,255,255,0.3)",
                            zIndex: 9999,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "12px",
                            pointerEvents: "none",
                        }, children: [_jsx(ReactQRCode, { value: currentAssetCode, size: 180, fgColor: "#00ffff", bgColor: "transparent" }), _jsx("span", { style: { color: "#fff", fontSize: "16px" }, children: currentAssetCode })] })), _jsx("style", { children: `
            .custom-table-container .ant-table,
            .custom-table-container .ant-table table {
              background-color: transparent !important;
              color: #FFFFFF !important;
              border: none !important;
              border-collapse: collapse !important;
              border-spacing: 0 !important;
            }
            .custom-table-container .ant-table-thead > tr > th {
              background-color: #062a58 !important;
              color: #00ffff !important;
              border: none !important;
              border-right: 1px solid #062a58 !important;
              border-left: 1px solid #062a58 !important;
              font-weight: 600;
              line-height: ${ROW_HEIGHT}px !important;
              padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
              font-size: ${REM_CONFIG.fontSize.small} !important;
            }
            .custom-table-container .ant-table-thead > tr > th:first-child {
              border-left: none !important;
            }
            .custom-table-container .ant-table-thead > tr > th:last-child {
              border-right: none !important;
            }
            .custom-table-container .ant-table-tbody > tr > td {
              color: #FFFFFF !important;
              border: none !important;
              line-height: ${ROW_HEIGHT}px !important;
              padding: 0 ${REM_CONFIG.spacing.paddingX}rem !important;
              font-size: ${REM_CONFIG.fontSize.small} !important;
            }
            .custom-table-container .ant-table-tbody > tr:hover > td {
              background-color: #1A446E !important;
            }
            .custom-table-container .ant-table-body {
              scrollbar-width: none;
              -ms-overflow-style: none;
              border: none !important;
              overflow-y: auto !important;
              height: auto !important;
              min-height: 0 !important;
              padding-top: 0 !important;
              margin-top: 0 !important;
            }
            .custom-table-container .ant-table-body::-webkit-scrollbar {
              display: none;
            }
            .custom-table-container .ant-table-container {
              border: none !important;
            }
            .custom-table-container .ant-table-thead::after,
            .custom-table-container .ant-table-cell::before {
              display: none !important;
            }

            /* ✅ 核心修复1：隐藏Antd测量空行（解决顶部空白） */
            .custom-table-container .ant-table-measure-row {
              display: none !important;
              height: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              border: none !important;
            }

            /* ✅ 核心修复2：清除tbody顶部间距，数据紧贴表头 */
            .custom-table-container .ant-table-tbody {
              margin-top: 0 !important;
              padding-top: 0 !important;
              border-top: none !important;
            }

            /* ✅ 核心修复3：无数据时完全隐藏占位，仅保留表头 */
            .custom-table-container .ant-table-placeholder {
              display: none !important;
              height: 0 !important;
              border: none !important;
            }
          ` })] })] }));
};
export default StoragePeriodStatistics;
